import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Zap, MapPin, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import UserBadges from "@/components/UserBadges";

export default function LeaderboardPage() {
  const [type, setType] = useState("wins");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const { data } = await api.get("/leaderboard", { params: { type } });
      setRows(data.leaderboard || []);
      setLoading(false);
    })();
  }, [type]);

  return (
    <div className="py-8 space-y-8">
      <div>
        <div className="overline">Who runs this hunt</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          Leader<span style={{ color: "#D7A62A" }}>board</span>
        </h1>
      </div>

      <div className="flex gap-2 border-b border-[#F5F2E8]/10">
        {[
          { k: "wins", l: "Top Finders", i: Trophy },
          { k: "fastest", l: "Fastest Finds", i: Zap },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setType(t.k)}
            className={`px-4 py-2 font-display uppercase tracking-wide text-sm flex items-center gap-2 ${
              type === t.k
                ? "text-[#76B82A] border-b-2 border-[#76B82A]"
                : "text-[#F5F2E8]/60"
            }`}
            data-testid={`lb-tab-${t.k}`}
          >
            <t.i size={14} /> {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#F5F2E8]/50">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="loot-card-flat p-12 text-center text-[#F5F2E8]/60">
          No rankings yet. Be the first.
        </div>
      ) : (
        <div className="loot-card-flat divide-y divide-[#F5F2E8]/10">
          {rows.map((r) => (
            <div
              key={`${r.rank}-${r.user_id}`}
              className="flex items-center justify-between p-4"
              data-testid={`lb-row-${r.rank}`}
            >
              <div className="flex items-center gap-4">
                <span
                  className="font-display text-3xl w-12"
                  style={{
                    color: r.rank === 1 ? "#D7A62A" : r.rank === 2 ? "#76B82A" : r.rank === 3 ? "#E53935" : "#F5F2E8",
                  }}
                >
                  {String(r.rank).padStart(2, "0")}
                </span>
                <div>
                  <div className="font-display uppercase text-xl">
                    {r.username ? (
                      <Link
                        to={`/u/${encodeURIComponent(r.username)}`}
                        className="hover:text-[#76B82A] transition-colors inline-flex items-center gap-2 flex-wrap"
                        data-testid={`lb-username-${r.rank}`}
                      >
                        @{r.username}
                        <UserBadges
                          lootLegend={r.user_loot_legend}
                          goodDeeds={r.user_good_deeds_count}
                          igniter={r.user_igniter}
                          badges={r.user_badges}
                          testIdPrefix={`lb-badge-${r.rank}`}
                        />
                      </Link>
                    ) : (
                      <>@{r.name}</>
                    )}
                  </div>
                  {type === "fastest" && (
                    <div className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-2 mt-1">
                      <MapPin size={10} /> {r.city} · {r.hide_amount ? "CASH" : `$${r.amount}`}
                    </div>
                  )}
                  {type === "wins" && (
                    <div className="font-mono text-xs text-[#F5F2E8]/50 mt-1">
                      {r.hide_amount ? "CASH total won" : `$${r.total_amount} total won`}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                {type === "wins" ? (
                  <>
                    <div className="font-display text-2xl" style={{ color: "#76B82A" }}>
                      {r.wins}
                    </div>
                    <div className="font-mono text-[10px] text-[#F5F2E8]/50 uppercase">Wins</div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-2xl flex items-center gap-1" style={{ color: "#D7A62A" }}>
                      <Clock size={18} /> {r.minutes}m
                    </div>
                    <div className="font-mono text-[10px] text-[#F5F2E8]/50 uppercase">To find</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Link to="/winners" className="font-mono text-xs text-[#76B82A]">
        ← Back to winners wall
      </Link>
    </div>
  );
}
